import { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';
import { format } from 'date-fns';
import { Store as StoreIcon, X, Mail, Check, AlertCircle, MoreVertical, Pause, Play, Trash2, RefreshCw } from 'lucide-react';
import ImageUpload from '../../components/ImageUpload';

interface Store {
  id: string;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  status: string;
  logoUrl?: string | null;
  createdAt: string;
  _count: {
    scans: number;
    admins: number;
  };
}

interface InviteForm {
  storeName: string;
  email: string;
}

export default function StoresPage() {
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState<InviteForm>({ storeName: '', email: '' });
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [manageDropdownId, setManageDropdownId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [resendStore, setResendStore] = useState<Store | null>(null);
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);

  useEffect(() => {
    loadStores();
  }, []);

  const loadStores = async () => {
    try {
      const response = await apiClient.get<{ success: boolean; data: Store[] }>('/admin/stores');
      setStores(response.data.data);
    } catch (error) {
      console.error('Failed to load stores:', error);
    } finally {
      setLoading(false);
    }
  };

  const openStoreDetail = (store: Store) => {
    setSelectedStore(store);
    setShowDetailModal(true);
  };

  const handleLogoUpload = async (base64Data: string) => {
    if (!selectedStore) return;

    const response = await apiClient.post<{ success: boolean; data: { logoUrl: string } }>(
      `/admin/stores/${selectedStore.id}/logo`,
      { image: base64Data }
    );

    // Update store in list
    setStores(stores.map(s =>
      s.id === selectedStore.id
        ? { ...s, logoUrl: response.data.data.logoUrl }
        : s
    ));

    // Update selected store
    setSelectedStore({ ...selectedStore, logoUrl: response.data.data.logoUrl });
  };

  const handleLogoDelete = async () => {
    if (!selectedStore) return;

    await apiClient.delete(`/admin/stores/${selectedStore.id}/logo`);

    // Update store in list
    setStores(stores.map(s =>
      s.id === selectedStore.id
        ? { ...s, logoUrl: null }
        : s
    ));

    // Update selected store
    setSelectedStore({ ...selectedStore, logoUrl: null });
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      ACTIVE: 'bg-green-100 text-green-800',
      PENDING: 'bg-yellow-100 text-yellow-800',
      PAUSED: 'bg-orange-100 text-orange-800',
      SUSPENDED: 'bg-red-100 text-red-800',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const handleInviteStore = async () => {
    if (!inviteForm.email) {
      setInviteResult({ type: 'error', message: 'Email is required' });
      return;
    }

    setInviting(true);
    setInviteResult(null);

    try {
      await apiClient.post('/admin/invites', {
        email: inviteForm.email,
        storeName: inviteForm.storeName || null,
      });

      setInviteResult({
        type: 'success',
        message: `Invitation sent to ${inviteForm.email}`,
      });

      // Clear form after success
      setInviteForm({ storeName: '', email: '' });

      // Refresh stores list to show new PENDING store
      await loadStores();

      // Close modal after delay
      setTimeout(() => {
        setShowInviteModal(false);
        setInviteResult(null);
      }, 2000);
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to send invitation';
      setInviteResult({ type: 'error', message });
    } finally {
      setInviting(false);
    }
  };

  const openInviteModal = () => {
    setInviteForm({ storeName: '', email: '' });
    setInviteResult(null);
    setShowInviteModal(true);
  };

  const toggleStoreStatus = async (store: Store) => {
    const newStatus = store.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    try {
      await apiClient.put(`/admin/stores/${store.id}/status`, { status: newStatus });
      setStores(stores.map(s => s.id === store.id ? { ...s, status: newStatus } : s));
      setManageDropdownId(null);
    } catch (error) {
      console.error('Failed to update store status:', error);
    }
  };

  const deleteStore = async (storeId: string) => {
    try {
      await apiClient.delete(`/admin/stores/${storeId}`);
      setStores(stores.filter(s => s.id !== storeId));
      setDeleteConfirmId(null);
      setManageDropdownId(null);
    } catch (error) {
      console.error('Failed to delete store:', error);
    }
  };

  const openResendModal = (store: Store) => {
    setResendStore(store);
    setResendEmail(store.email);
    setManageDropdownId(null);
  };

  const handleResendInvite = async () => {
    if (!resendStore) return;
    setResending(true);
    try {
      await apiClient.post(`/admin/stores/${resendStore.id}/resend-invite`, {
        email: resendEmail !== resendStore.email ? resendEmail : undefined,
      });
      // Update store email in list if changed
      if (resendEmail !== resendStore.email) {
        setStores(stores.map(s => s.id === resendStore.id ? { ...s, email: resendEmail } : s));
      }
      setResendStore(null);
    } catch (error: any) {
      console.error('Failed to resend invite:', error);
      alert(error.response?.data?.error || 'Failed to resend invite');
    } finally {
      setResending(false);
    }
  };

  // Calculate status counts
  const activeCount = stores.filter(s => s.status === 'ACTIVE').length;
  const pendingCount = stores.filter(s => s.status === 'PENDING').length;
  const pausedCount = stores.filter(s => s.status === 'PAUSED').length;
  const suspendedCount = stores.filter(s => s.status === 'SUSPENDED').length;

  if (loading) {
    return <div className="animate-pulse">Loading...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Participating Stores</h1>
        <button onClick={openInviteModal} className="btn-primary">Invite Store</button>
      </div>

      {/* Status Pills */}
      <div className="flex gap-3 mb-6">
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
          Active: {activeCount}
        </span>
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
          Pending: {pendingCount}
        </span>
        {pausedCount > 0 && (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
            Paused: {pausedCount}
          </span>
        )}
        {suspendedCount > 0 && (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
            Suspended: {suspendedCount}
          </span>
        )}
      </div>

      <div className="card">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Store
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total Scans
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Joined
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {stores.map((store) => (
              <tr key={store.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden">
                      {store.logoUrl ? (
                        <img src={store.logoUrl} alt={store.name} className="w-full h-full object-cover" />
                      ) : (
                        <StoreIcon className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{store.name}</div>
                      <div className="text-sm text-gray-500">{store.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(store.status)}`}>
                    {store.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {store._count.scans}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {format(new Date(store.createdAt), 'MMM d, yyyy')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openStoreDetail(store)}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      View
                    </button>
                    <div className="relative">
                      <button
                        onClick={() => setManageDropdownId(manageDropdownId === store.id ? null : store.id)}
                        className="p-1 text-gray-500 hover:text-gray-700 rounded hover:bg-gray-100"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      {manageDropdownId === store.id && (
                        <div className="absolute right-0 mt-1 w-40 bg-white rounded-md shadow-lg border z-10">
                          {store.status === 'PENDING' && (
                            <button
                              onClick={() => openResendModal(store)}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                            >
                              <RefreshCw className="w-4 h-4 text-blue-500" />
                              Resend Invite
                            </button>
                          )}
                          {store.status !== 'PENDING' && (
                            <button
                              onClick={() => toggleStoreStatus(store)}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                            >
                              {store.status === 'ACTIVE' ? (
                                <>
                                  <Pause className="w-4 h-4 text-orange-500" />
                                  Pause
                                </>
                              ) : (
                                <>
                                  <Play className="w-4 h-4 text-green-500" />
                                  Activate
                                </>
                              )}
                            </button>
                          )}
                          <button
                            onClick={() => setDeleteConfirmId(store.id)}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {stores.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No stores yet. Invite your first store to get started.
          </div>
        )}
      </div>

      {/* Store Detail Modal */}
      {showDetailModal && selectedStore && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-xl font-bold text-gray-900">{selectedStore.name}</h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Logo Upload */}
            <ImageUpload
              currentImageUrl={selectedStore.logoUrl}
              onUpload={handleLogoUpload}
              onDelete={handleLogoDelete}
              label="Store Logo"
              aspectRatio="square"
              className="mb-6"
            />

            {/* Store Info */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Email</label>
                <p className="text-gray-900">{selectedStore.email}</p>
              </div>
              {selectedStore.phone && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Phone</label>
                  <p className="text-gray-900">{selectedStore.phone}</p>
                </div>
              )}
              {(selectedStore.address || selectedStore.city) && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Address</label>
                  <p className="text-gray-900">
                    {selectedStore.address}
                    {selectedStore.city && `, ${selectedStore.city}`}
                    {selectedStore.state && `, ${selectedStore.state}`}
                  </p>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <p>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(selectedStore.status)}`}>
                    {selectedStore.status}
                  </span>
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Total Scans</label>
                  <p className="text-2xl font-bold text-gray-900">{selectedStore._count.scans}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Admins</label>
                  <p className="text-2xl font-bold text-gray-900">{selectedStore._count.admins}</p>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Joined</label>
                <p className="text-gray-900">{format(new Date(selectedStore.createdAt), 'MMMM d, yyyy')}</p>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowDetailModal(false)}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Store Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-start mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Invite Store</h2>
                  <p className="text-sm text-gray-500">Send an invitation to join</p>
                </div>
              </div>
              <button
                onClick={() => setShowInviteModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {inviteResult && (
              <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
                inviteResult.type === 'success'
                  ? 'bg-green-50 text-green-800'
                  : 'bg-red-50 text-red-800'
              }`}>
                {inviteResult.type === 'success' ? (
                  <Check className="w-5 h-5" />
                ) : (
                  <AlertCircle className="w-5 h-5" />
                )}
                <span>{inviteResult.message}</span>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="label">Store Name</label>
                <input
                  type="text"
                  value={inviteForm.storeName}
                  onChange={(e) => setInviteForm({ ...inviteForm, storeName: e.target.value })}
                  className="input"
                  placeholder="e.g., Bob's Coffee Shop"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Optional. The store owner can change this later.
                </p>
              </div>
              <div>
                <label className="label">Email Address *</label>
                <input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  className="input"
                  placeholder="store@example.com"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  An invitation link will be sent to this email.
                </p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>What happens next:</strong>
              </p>
              <ul className="text-sm text-gray-500 mt-2 space-y-1">
                <li>1. The store owner receives an email invitation</li>
                <li>2. They click the link to set up their account</li>
                <li>3. They can sign in with Google or email</li>
                <li>4. Their store appears here once registered</li>
              </ul>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowInviteModal(false)}
                className="btn-secondary"
                disabled={inviting}
              >
                Cancel
              </button>
              <button
                onClick={handleInviteStore}
                disabled={inviting || !inviteForm.email}
                className="btn-primary"
              >
                {inviting ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Store</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete this store? This will also delete all associated QR codes, scans, and data. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteStore(deleteConfirmId)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resend Invite Modal */}
      {resendStore && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-start mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Resend Invite</h2>
                  <p className="text-sm text-gray-500">{resendStore.name}</p>
                </div>
              </div>
              <button
                onClick={() => setResendStore(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="label">Email Address</label>
                <input
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  className="input"
                  placeholder="store@example.com"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Change the email to send the invite to a different address.
                </p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                A new invitation link will be sent and the previous link will be invalidated.
              </p>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setResendStore(null)}
                className="btn-secondary"
                disabled={resending}
              >
                Cancel
              </button>
              <button
                onClick={handleResendInvite}
                disabled={resending || !resendEmail}
                className="btn-primary"
              >
                {resending ? 'Sending...' : 'Resend Invite'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
